import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  from,
  ApolloProvider,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, type PropsWithChildren } from 'react';
import { AUTH0_AUDIENCE, GRAPHQL_URL } from '~/shared/config';
import { SENTRY_OPERATIONS } from '~/shared/constants';
import { reportError } from '~/shared/utils/sentry';

const httpLink = new HttpLink({
  uri: GRAPHQL_URL,
  credentials: 'include', // Required to send/receive httpOnly cookies
});

export function AuthApolloProvider({ children }: PropsWithChildren<{}>) {
  const { getAccessTokenSilently } = useAuth0();

  const client = useMemo(() => {
    const authLink = setContext(async (_, { headers }) => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: AUTH0_AUDIENCE,
            scope: 'openid profile email offline_access',
          },
        });

        return {
          headers: {
            ...headers,
            Authorization: `Bearer ${token}`,
          },
        };
      } catch (error) {
        // If Auth0 token retrieval fails, that's okay. The user might be
        // authenticated via the programmatic token cookie instead
        reportError(error, {
          operation: SENTRY_OPERATIONS.APOLLO_GET_ACCESS_TOKEN,
        });

        return { headers };
      }
    });

    return new ApolloClient({
      link: from([authLink, httpLink]),
      cache: new InMemoryCache(),
    });
  }, [getAccessTokenSilently]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
